///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Platform tuning and page-lifecycle handling for the engine: lower idle
/// draw rate and drawing-buffer scale on phones, and an explicit pause
/// while the page is hidden (a backgrounded tab or app) with a clean
/// resume. Web and native shells share this; nothing here touches
/// Capacitor (visibilitychange fires in WKWebView and Android WebView too).
///

import { isMobile } from "@/earthbrowser/utils/platform"
import type { Stovokor } from "@stovokor/Stovokor"
import type { StovokorContext } from "@stovokor/StovokorContext"

/**
 * Idle draw rate on phones. The idle floor exists for things the engine
 * can't see changing (wall-clock shader time, material uniforms animated
 * by frame tasks); on a battery, half the desktop rate is plenty for those
 */
const cMobileIdleFrameRate = 15

/**
 * Drawing-buffer scale caps. iPhones (3x displays) render well at 2; the
 * mid-range GPUs behind most Android 2.5-3x panels are fill-rate bound at
 * 2, and 1.5 is not distinguishable on a 6-inch screen
 */
const cAndroidMaxPixelRatio = 1.5

/** Apply the phone profile when running on one (no-op on desktop) */
export function applyMobileEngineTuning(engine: Stovokor, context: StovokorContext): void {
	if (!isMobile()) return
	engine.idleFrameRate = cMobileIdleFrameRate
	if (/Android/i.test(navigator.userAgent)) {
		context.maxPixelRatio = Math.min(context.maxPixelRatio, cAndroidMaxPixelRatio)
	}
}

/**
 * Pause the engine while the document is hidden and resume it when shown.
 * Browsers already starve requestAnimationFrame in the background, so this
 * mostly makes the state explicit; what matters is the resume: a fresh
 * frame is requested so the first visible tick draws, and the springs
 * (which run on absolute time) take one bounded catch-up step rather than
 * integrating the whole gap (spring.ts). Returns a disposer
 */
export function installVisibilityLifecycle(engine: Stovokor): () => void {
	if (typeof document === "undefined") return () => {}
	let pausedByLifecycle = false
	const onVisibilityChange = () => {
		if (document.visibilityState === "hidden") {
			if (engine.isRunning) {
				engine.pause()
				pausedByLifecycle = true
			}
		} else if (pausedByLifecycle) {
			pausedByLifecycle = false
			engine.run()
			engine.requestRender()
		}
	}
	document.addEventListener("visibilitychange", onVisibilityChange)
	return () => document.removeEventListener("visibilitychange", onVisibilityChange)
}
